import React, { useCallback, useEffect, useMemo, useState } from "react";
import { similarApi } from "../api/auctiongate.js";

/**
 * Vehicle Auction History — free-form search over stat.vin sales index.
 *
 * Backend endpoints:
 *   POST /api/similar/cars     → /similar-cars/index
 *   POST /api/similar/filters  → /similar-cars/filter-list
 *
 * stat.vin reliably accepts: api_token, make, model, year, sale_date_from,
 * sale_date_to, page, per_page, primary_damage, secondary_damage,
 * engine_type, drive. Other params (price/odometer/auction) are best-effort
 * — the upstream silently ignores unknown filters.
 */

const AUCTIONS = [
  { value: "iaai", label: "IAAI United States" },
  { value: "copart", label: "COPART USA" },
  { value: "copart_canada", label: "COPART Canada" },
  { value: "iaai_canada", label: "IAAI Canada" },
];

function ymd(d) {
  if (!d) return "";
  const date = d instanceof Date ? d : new Date(d);
  if (isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${y}${m}${day}`;
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
function ninetyDaysAgoISO() {
  const d = new Date();
  d.setDate(d.getDate() - 90);
  return d.toISOString().slice(0, 10);
}

const initialFilters = {
  saleStatus: "sold", // "sold" | "active"
  make: "",
  model: "",
  yearFrom: "",
  yearTo: "",
  priceMin: "",
  priceMax: "",
  dateFrom: ninetyDaysAgoISO(),
  dateTo: todayISO(),
  fuelType: "",
  drive: "",
  odometerMin: "",
  odometerMax: "",
  primaryDamage: "",
  auctions: ["iaai", "copart"],
};

export default function HistoryPage() {
  const [filters, setFilters] = useState(initialFilters);
  const [filterOptions, setFilterOptions] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  // Load filter options (engine_type, drive, primary_damage) once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await similarApi.filters({});
        if (cancelled) return;
        const fl = r?.data || r;
        setFilterOptions({
          engineType: arr(fl?.engine_type),
          drive: arr(fl?.drive),
          primaryDamage: arr(fl?.primary_damage),
        });
      } catch {
        // upstream may require make+year; ignore — fields fall back to text input.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const set = (key, val) => setFilters((f) => ({ ...f, [key]: val }));

  const toggleAuction = (val) => {
    setFilters((f) => ({
      ...f,
      auctions: f.auctions.includes(val)
        ? f.auctions.filter((a) => a !== val)
        : [...f.auctions, val],
    }));
  };

  const clear = () => {
    setFilters(initialFilters);
    setData(null);
    setError(null);
    setPage(1);
  };

  const buildPayload = useCallback(
    (pageNum) => {
      const body = {
        page: pageNum,
        per_page: perPage,
      };
      if (filters.make.trim()) body.make = filters.make.trim().toUpperCase();
      if (filters.model.trim()) body.model = filters.model.trim().toUpperCase();
      if (filters.yearFrom) body.year_from = String(filters.yearFrom);
      if (filters.yearTo) body.year_to = String(filters.yearTo);
      if (filters.priceMin) body.price_from = String(filters.priceMin);
      if (filters.priceMax) body.price_to = String(filters.priceMax);
      if (filters.odometerMin) body.odometer_from = String(filters.odometerMin);
      if (filters.odometerMax) body.odometer_to = String(filters.odometerMax);
      if (filters.fuelType) body.engine_type = filters.fuelType;
      if (filters.drive) body.drive = filters.drive;
      if (filters.primaryDamage) body.primary_damage = filters.primaryDamage;
      if (filters.dateFrom) body.sale_date_from = ymd(filters.dateFrom);
      if (filters.dateTo) body.sale_date_to = ymd(filters.dateTo);
      if (filters.auctions.length === 1) body.auction = filters.auctions[0];
      else if (filters.auctions.length > 1) body.auctions = filters.auctions;
      return body;
    },
    [filters, perPage]
  );

  const search = useCallback(
    async (pageNum = 1) => {
      setLoading(true);
      setError(null);
      try {
        const r = await similarApi.cars(buildPayload(pageNum));
        setData(r);
        setPage(pageNum);
      } catch (e) {
        setError(e?.message || "Search failed");
        setData(null);
      } finally {
        setLoading(false);
      }
    },
    [buildPayload]
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    search(1);
  };

  const items = extractItems(data);
  const total = data?.pagination?.total ?? data?.total ?? items.length;
  const totalPages = data?.pagination?.total_pages ?? data?.total_pages ?? 1;

  return (
    <div className="container stack-lg">
      <div className="page-hero">
        <h1>Vehicle auction history · Copart, IAAI</h1>
        <p>
          Search past auction sales by make, model, year, condition, and date.
          Powered by stat.vin sales index.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="card">
        {/* Sold/Active toggle */}
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          <button
            type="button"
            className={`badge${filters.saleStatus === "sold" ? " info" : ""}`}
            style={pillStyle(filters.saleStatus === "sold")}
            onClick={() => set("saleStatus", "sold")}
          >
            Sold
          </button>
          <button
            type="button"
            className="badge"
            style={pillStyle(filters.saleStatus === "active")}
            onClick={() => set("saleStatus", "active")}
          >
            Active
          </button>
        </div>

        <div className="grid grid-3" style={{ alignItems: "start" }}>
          {/* Column 1: Make & Model */}
          <FieldGroup title="Make & Model Details">
            <Field label="Make">
              <input
                type="text"
                placeholder="e.g. TOYOTA"
                value={filters.make}
                onChange={(e) => set("make", e.target.value)}
                style={inp}
              />
            </Field>
            <Field label="Model">
              <input
                type="text"
                placeholder="e.g. CAMRY"
                value={filters.model}
                onChange={(e) => set("model", e.target.value)}
                style={inp}
              />
            </Field>
            <Field label="Year">
              <Range>
                <input
                  type="number"
                  placeholder="From"
                  value={filters.yearFrom}
                  onChange={(e) => set("yearFrom", e.target.value)}
                  style={inp}
                />
                <span className="muted">to</span>
                <input
                  type="number"
                  placeholder="To"
                  value={filters.yearTo}
                  onChange={(e) => set("yearTo", e.target.value)}
                  style={inp}
                />
              </Range>
            </Field>
            <Field label="Price (USD)">
              <Range>
                <input
                  type="number"
                  placeholder="Min"
                  value={filters.priceMin}
                  onChange={(e) => set("priceMin", e.target.value)}
                  style={inp}
                />
                <span className="muted">to</span>
                <input
                  type="number"
                  placeholder="Max"
                  value={filters.priceMax}
                  onChange={(e) => set("priceMax", e.target.value)}
                  style={inp}
                />
              </Range>
            </Field>
            <Field label="Sale date">
              <Range>
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => set("dateFrom", e.target.value)}
                  style={inp}
                />
                <span className="muted">to</span>
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => set("dateTo", e.target.value)}
                  style={inp}
                />
              </Range>
            </Field>
          </FieldGroup>

          {/* Column 2: Vehicle Details */}
          <FieldGroup title="Vehicle Details">
            <Field label="Fuel Type">
              <select
                value={filters.fuelType}
                onChange={(e) => set("fuelType", e.target.value)}
                style={inp}
              >
                <option value="">— Any —</option>
                {(filterOptions?.engineType ?? []).map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Drive">
              <select
                value={filters.drive}
                onChange={(e) => set("drive", e.target.value)}
                style={inp}
              >
                <option value="">— Any —</option>
                {(filterOptions?.drive ?? []).map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Odometer (miles)">
              <Range>
                <input
                  type="number"
                  placeholder="Min"
                  value={filters.odometerMin}
                  onChange={(e) => set("odometerMin", e.target.value)}
                  style={inp}
                />
                <span className="muted">to</span>
                <input
                  type="number"
                  placeholder="Max"
                  value={filters.odometerMax}
                  onChange={(e) => set("odometerMax", e.target.value)}
                  style={inp}
                />
              </Range>
            </Field>
            <Field label="Primary damage">
              <select
                value={filters.primaryDamage}
                onChange={(e) => set("primaryDamage", e.target.value)}
                style={inp}
              >
                <option value="">— Any —</option>
                {(filterOptions?.primaryDamage ?? []).map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </Field>
          </FieldGroup>

          {/* Column 3: Auctions */}
          <FieldGroup title="Auctions">
            <div className="stack" style={{ gap: 8 }}>
              {AUCTIONS.map((a) => (
                <label
                  key={a.value}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={filters.auctions.includes(a.value)}
                    onChange={() => toggleAuction(a.value)}
                  />
                  <span>{a.label}</span>
                </label>
              ))}
            </div>
          </FieldGroup>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            marginTop: 18,
          }}
        >
          <button type="button" className="btn btn-ghost" onClick={clear}>
            Clear
          </button>
          <button type="submit" className="btn btn-accent" disabled={loading}>
            {loading ? <span className="loader" /> : "Search"}
          </button>
        </div>
      </form>

      {error && <div className="card login-error">{error}</div>}

      {data && !error && (
        <ResultsTable
          items={items}
          total={total}
          page={page}
          totalPages={totalPages}
          loading={loading}
          onPage={(p) => search(p)}
        />
      )}

      {!data && !loading && !error && (
        <div className="placeholder-panel">
          Set your filters above and click <b>Search</b> to pull auction
          history. Tip: at minimum specify a Make and a year range for relevant
          results.
        </div>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────── helpers ───────────────────── */

function arr(x) {
  return Array.isArray(x) ? x.filter(Boolean) : [];
}

function extractItems(d) {
  if (!d) return [];
  if (Array.isArray(d)) return d;
  if (Array.isArray(d.data)) return d.data;
  if (Array.isArray(d?.data?.data)) return d.data.data;
  if (Array.isArray(d?.items)) return d.items;
  return [];
}

function FieldGroup({ title, children }) {
  return (
    <div>
      <h3 className="section-title" style={{ fontSize: 14, marginBottom: 10 }}>
        {title}
      </h3>
      <div className="stack" style={{ gap: 12 }}>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="muted small" style={{ display: "block", marginBottom: 4 }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function Range({ children }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr auto 1fr",
        alignItems: "center",
        gap: 8,
      }}
    >
      {children}
    </div>
  );
}

const inp = {
  width: "100%",
  padding: "8px 10px",
  border: "1px solid var(--color-border-input)",
  borderRadius: 8,
  fontSize: 13,
  boxSizing: "border-box",
};

function pillStyle(active) {
  return {
    padding: "6px 14px",
    border: "1px solid var(--color-border)",
    borderRadius: 999,
    cursor: "pointer",
    background: active ? "var(--color-primary)" : "transparent",
    color: active ? "#fff" : "var(--color-text)",
    fontWeight: active ? 600 : 400,
  };
}

function ResultsTable({ items, total, page, totalPages, loading, onPage }) {
  const rows = useMemo(
    () =>
      items.map((it, i) => {
        const sale = it.sales_history?.[0] ?? {};
        return {
          key: it.id ?? it.lot_number ?? i,
          vin: it.vin || "—",
          year: it.year || sale.year || "",
          make: it.make || sale.make || "",
          model: it.model || sale.model || "",
          auction: it.auction || sale.auction || "",
          lot: it.lot_number || sale.lot_number || "",
          mileage: it.odometer || sale.odometer || "",
          damage: it.primary_damage || sale.primary_damage || "",
          price: it.purchase_price || sale.purchase_price || sale.bid || 0,
          status: sale.status || it.status || "Sold",
          saleDate: sale.sale_date || it.sale_date || "",
        };
      }),
    [items]
  );

  return (
    <div className="card">
      <div
        className="card__title"
        style={{ display: "flex", justifyContent: "space-between" }}
      >
        <span>Results</span>
        <span className="muted small">
          {total} record{total === 1 ? "" : "s"}
        </span>
      </div>

      {rows.length === 0 ? (
        <div className="placeholder-panel">No results match your filters.</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: 13,
            }}
          >
            <thead>
              <tr style={{ textAlign: "left", color: "var(--color-text-muted)" }}>
                <th style={th}>VIN</th>
                <th style={th}>Year / Make / Model</th>
                <th style={th}>Auction · Lot</th>
                <th style={th}>Mileage</th>
                <th style={th}>Damage</th>
                <th style={th}>Sale date</th>
                <th style={{ ...th, textAlign: "right" }}>Price</th>
                <th style={th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.key}
                  style={{ borderTop: "1px solid var(--color-border)" }}
                >
                  <td style={{ ...td, fontFamily: "monospace" }}>{r.vin}</td>
                  <td style={td}>
                    {[r.year, r.make, r.model].filter(Boolean).join(" ")}
                  </td>
                  <td style={td}>
                    {r.auction}
                    {r.lot && (
                      <span className="muted">
                        {" · "}
                        {r.lot}
                      </span>
                    )}
                  </td>
                  <td style={td}>{fmtNum(r.mileage)}</td>
                  <td style={td}>{r.damage || "—"}</td>
                  <td style={td}>{r.saleDate || "—"}</td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 600 }}>
                    {r.price ? `$${fmtNum(r.price)}` : "—"}
                  </td>
                  <td style={td}>
                    <span className="badge">{r.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div
          style={{
            display: "flex",
            gap: 8,
            justifyContent: "center",
            alignItems: "center",
            marginTop: 12,
          }}
        >
          <button
            className="btn btn-ghost"
            disabled={loading || page <= 1}
            onClick={() => onPage(page - 1)}
          >
            ← Prev
          </button>
          <span className="muted small">
            Page <b>{page}</b> of {totalPages}
          </span>
          <button
            className="btn btn-ghost"
            disabled={loading || page >= totalPages}
            onClick={() => onPage(page + 1)}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}

const th = {
  padding: "10px 8px",
  fontWeight: 600,
  fontSize: 12,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

const td = {
  padding: "10px 8px",
  verticalAlign: "top",
};

function fmtNum(n) {
  if (n === null || n === undefined || n === "") return "";
  const num = Number(n);
  if (!Number.isFinite(num)) return String(n);
  return new Intl.NumberFormat("en-US").format(num);
}
